import React, { Fragment, useEffect, useState, useMemo } from "react";
import { CardBody, Col, Row, Table, Input, Label, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Card, CardHeader } from "reactstrap";
import { Link } from "react-router-dom";
import { useReactTable, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import { rankItem } from '@tanstack/match-sorter-utils';
import moment from "moment";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logo from '../../../assets/images/logos/logo_preta.jpeg';
import { ToastContainer } from 'react-toastify';
import { toast } from 'react-toastify';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const formatarData = (dataString) => {
  if (!dataString) return '--';
  try {
    return moment(dataString).format('DD/MM/YYYY HH:mm');
  } catch (e) {
    return '--';
  }
};

const formatarMoeda = (valor) => {
  if (valor === null || valor === undefined) return 'R$ --';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
};

const formatNumber = (value) => {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
};

const getNestedValue = (obj, path, defaultValue = null) => {
    if (!path) return defaultValue;
    const value = path.split('.').reduce((acc, part) => acc && acc[part], obj);
    return value !== undefined && value !== null ? value : defaultValue;
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;

  if (typeof value === 'string') {
    const normalized = value
      .replace(/R\$/g, '')
      .replace(/\s/g, '')
      .replace(/\./g, '')
      .replace(',', '.');

    return Number(normalized) || 0;
  }

  return Number(value) || 0;
};

const hasValor = (value) => {
  return value !== null && value !== undefined && value !== '';
};

const getValorCalculadoItens = (item = {}) => {
  if (hasValor(getNestedValue(item, 'estande_detalhado.valor_final_calculado'))) {
    return toNumber(getNestedValue(item, 'estande_detalhado.valor_final_calculado'));
  }

  if (hasValor(getNestedValue(item, 'valor_final_calculado'))) {
    return toNumber(getNestedValue(item, 'valor_final_calculado'));
  }

  if (hasValor(getNestedValue(item, 'estande.valor_total'))) {
    return toNumber(getNestedValue(item, 'estande.valor_total'));
  }

  const valorEspaco = toNumber(getNestedValue(item, 'estande_detalhado.valor_total'));
  const valorKVA = toNumber(getNestedValue(item, 'estande_detalhado.valor_kva'));
  const valorEstacionamento = toNumber(getNestedValue(item, 'estande_detalhado.valor_estacionamento'));
  const valorAdicionais = toNumber(getNestedValue(item, 'estande_detalhado.valor_total_adicionais'));

  return valorEspaco + valorKVA + valorEstacionamento + valorAdicionais;
};

const getValorFinanceiroReal = (item = {}) => {
  if (hasValor(getNestedValue(item, 'valor_total_financeiro'))) {
    return toNumber(getNestedValue(item, 'valor_total_financeiro'));
  }

  if (hasValor(getNestedValue(item, 'valor_final_para_exportacao'))) {
    return toNumber(getNestedValue(item, 'valor_final_para_exportacao'));
  }

  if (hasValor(getNestedValue(item, 'valor_total_geral_estande'))) {
    return toNumber(getNestedValue(item, 'valor_total_geral_estande'));
  }

  return getValorCalculadoItens(item);
};

const getDiferencaFinanceira = (item = {}) => {
  return getValorFinanceiroReal(item) - getValorCalculadoItens(item);
};

const normalizarStatus = (valor = '') => String(valor)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase();

const getUltimaEtapaNome = (item = {}) => {
  const etapas = Array.isArray(item.etapas) ? item.etapas : [];
  return etapas[etapas.length - 1]?.etapa_reserva?.nome || '';
};

const isReservaCancelada = (item = {}) => {
  const flagCancelado = item.cancelado;
  if (
    flagCancelado === true ||
    flagCancelado === 1 ||
    String(flagCancelado).toLowerCase() === 'true'
  ) {
    return true;
  }

  const ultimaEtapa = normalizarStatus(getUltimaEtapaNome(item));
  return ultimaEtapa === 'cancelada' || ultimaEtapa === 'cancelado';
};

const getStatusReserva = (item = {}) => {
  if (isReservaCancelada(item)) return 'Cancelada';
  return getUltimaEtapaNome(item) || 'Sem Etapa';
};

const isFiltroCanceladas = (statusSelecionado = '') => {
  const status = normalizarStatus(statusSelecionado);
  return status === 'cancelada' || status === 'cancelado';
};

const TabelaFiltroPaginacao = ({
  columns, data, isGlobalFilter, customPageSize, tableClassName, theadClassName,
  trClassName, thClassName, divClassName, SearchPlaceholder, filters = [],
  nomeRelatorio, hiddenColumns = [], feirasOptions = [], feiraSelecionada, onFeiraChange,
  paisesOptions = [], paisSelecionado, onPaisChange, estadosOptions = [], estadoSelecionado,
  onEstadoChange, cidadesOptions = [], cidadeSelecionada, onCidadeChange,
  pavilhoesOptions = [], pavilhaoSelecionado, onPavilhaoChange, dashboard,
  statusOptions = [], statusSelecionado, onStatusChange, gerouVenda
}) => {
  
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isExportacaoAvancadaOpen, setIsExportacaoAvancadaOpen] = useState(false);
  const [camposSelecionados, setCamposSelecionados] = useState([]);
  const [ordenacao, setOrdenacao] = useState('updated_at');
  const [ordemDescendente, setOrdemDescendente] = useState(true);
  const [agrupamento, setAgrupamento] = useState('');

  const camposDasColunas = useMemo(() => {
    return columns
      .filter(col => col.header && col.accessorKey && !col.header.includes('Ação'))
      .map(col => {
        let formatter = (value) => value ?? '--';
        if (['reserva_valida_ate', 'created_at', 'updated_at'].includes(col.accessorKey)) {
          formatter = formatarData;
        } else if (col.accessorKey.toLowerCase().includes('valor')) {
          formatter = formatarMoeda;
        } else if (col.accessorKey === 'etapas') {
          formatter = (item) => {
            if (!item || !Array.isArray(item)) return '--';
            const ultimaEtapa = item[item.length - 1]?.etapa_reserva?.nome;
            return ultimaEtapa || '--';
          };
        }
        const accessor = (item) => {
          if (col.accessorKey === 'etapas') {
            return getStatusReserva(item);
          }

          return formatter(getNestedValue(item, col.accessorKey));
        };
        return { id: col.accessorKey, label: col.header, accessor };
      });
  }, [columns]);

  const camposAdicionais = useMemo(() => [
    { id: 'expositor.cnpj_codigo_internacional', label: 'CNPJ/Cód. Expositor', accessor: item => getNestedValue(item, 'expositor.cnpj_codigo_internacional') },
    { id: 'expositor.nome_fantasia', label: 'Nome Fantasia Expositor', accessor: item => getNestedValue(item, 'expositor.nome_fantasia') },
    { id: 'expositor.endereco', label: 'Endereço Expositor', accessor: item => getNestedValue(item, 'expositor.endereco') },
    { id: 'expositor.email', label: 'E-mail Expositor', accessor: item => getNestedValue(item, 'expositor.email') },
    { id: 'expositor.telefone', label: 'Telefone Expositor', accessor: item => getNestedValue(item, 'expositor.telefone') },
    { id: 'expositor.whatsapp', label: 'Whatsapp Expositor', accessor: item => getNestedValue(item, 'expositor.whatsapp') },
    { id: 'estande_detalhado.valor', label: 'Valor Base Espaço', accessor: item => formatarMoeda(getNestedValue(item, 'estande_detalhado.valor', 0)), type: 'currency' }, // <-- ADICIONADO type: 'currency'
    { id: 'estande_detalhado.area', label: 'Área (m²)', accessor: item => formatNumber(getNestedValue(item, 'estande_detalhado.area', 0)), type: 'number' }, // <-- ADICIONADO type: 'number'
    { id: 'estande_detalhado.metragem_bonificada', label: 'M² Bonificada', accessor: item => formatNumber(getNestedValue(item, 'estande_detalhado.metragem_bonificada', 0)), type: 'number' }, // <-- ADICIONADO type: 'number'
    { id: 'estande_detalhado.valor_kva', label: 'Valor KVA', accessor: item => formatarMoeda(getNestedValue(item, 'estande_detalhado.valor_kva', 0)), type: 'currency' }, // <-- ADICIONADO type: 'currency'
    { id: 'estande_detalhado.quantidade_kva_tabela', label: 'Quantidade KVA', accessor: item => formatNumber(getNestedValue(item, 'estande_detalhado.quantidade_kva_tabela', 0)), type: 'number' },
    { id: 'estande_detalhado.valor_estacionamento', label: 'Valor Estacionamento', accessor: item => formatarMoeda(getNestedValue(item, 'estande_detalhado.valor_estacionamento', 0)), type: 'currency' }, // <-- ADICIONADO type: 'currency'
    { id: 'estande_detalhado.quantidade_estacionamento', label: 'Quantidade Estacionamento', accessor: item => formatNumber(getNestedValue(item, 'estande_detalhado.quantidade_estacionamento', 0)), type: 'number' },
    { id: 'estande_detalhado.valor_total_adicionais', label: 'Valor Adicionais', accessor: item => formatarMoeda(getNestedValue(item, 'estande_detalhado.valor_total_adicionais', 0)), type: 'currency' }, // <-- ADICIONADO type: 'currency'
    { id: 'estande_detalhado.valor_final_calculado', label: 'Valor Final Calculado', accessor: item => formatarMoeda(getNestedValue(item, 'estande_detalhado.valor_final_calculado', 0)), type: 'currency' }, // <-- ADICIONADO type: 'currency'
    { id: 'valor_financeiro_real', label: 'Valor Contratado / Financeiro', accessor: item => formatarMoeda(getValorFinanceiroReal(item)), getRawValue: item => getValorFinanceiroReal(item), type: 'currency' },
    { id: 'valor_calculado_itens', label: 'Valor Calculado dos Itens', accessor: item => formatarMoeda(getValorCalculadoItens(item)), getRawValue: item => getValorCalculadoItens(item), type: 'currency' },
    { id: 'diferenca_negociacao', label: 'Diferença por Negociação', accessor: item => formatarMoeda(getDiferencaFinanceira(item)), getRawValue: item => getDiferencaFinanceira(item), type: 'currency' },
    ], []);

  const camposDisponiveis = useMemo(() => [...camposDasColunas, ...camposAdicionais], [camposDasColunas, camposAdicionais]);

  const columnVisibility = useMemo(() => hiddenColumns.reduce((acc, colId) => ({ ...acc, [colId]: false }), {}), [hiddenColumns]);

  useEffect(() => {
    if (camposDasColunas.length > 0 && camposSelecionados.length === 0) {
      setCamposSelecionados(camposDasColunas.map(c => c.id));
    }
  }, [camposDasColunas]);

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const stringValue = String(getNestedValue(row.original, columnId, ''));
    const itemRank = rankItem(stringValue, value);
    addMeta({ itemRank });
    return itemRank.passed;
  };
  
  const globalFuzzyFilter = (row, columnIds, filterValue) => {
    if (!filterValue) return true;
    
    const searchTerm = filterValue.toLowerCase();
    const item = row.original;
    
    const searchFields = [
      getNestedValue(item, 'estande_detalhado.nome', ''), // Nome do estande
      getNestedValue(item, 'expositor.nome', ''), // Nome do expositor
      getNestedValue(item, 'expositor.nome_mapa', ''), // Nome do expositor
      getNestedValue(item, 'expositor.nome_fantasia', ''), // Nome do expositor
      getNestedValue(item, 'comercial.nome', ''), // Nome do vendedor
      getNestedValue(item, 'expositor.cidade', ''), // Nome da cidade
      getNestedValue(item, 'expositor.estado', '') // Nome do estado
    ].map(value => String(value).toLowerCase());
    
    // Verifica se algum dos campos contém o termo de busca
    return searchFields.some(field => field.includes(searchTerm));
  };

  const exactFilter = (row, columnId, value) => {
    if (!value) return true;
    const cellValue = getNestedValue(row.original, columnId, '');
    return String(cellValue) === value;
  };

  const table = useReactTable({
    columns, data, filterFns: { fuzzy: fuzzyFilter, exact: exactFilter },
    initialState: { columnVisibility },
    state: { columnFilters, globalFilter },
    onColumnFiltersChange: setColumnFilters, onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: globalFuzzyFilter, getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(), getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    table.getColumn('pais')?.setFilterValue(paisSelecionado || undefined);
    table.getColumn('estado')?.setFilterValue(estadoSelecionado || undefined);
    table.getColumn('cidade')?.setFilterValue(cidadeSelecionada || undefined);
    table.getColumn('pavilhao')?.setFilterValue(pavilhaoSelecionado || undefined);
  }, [paisSelecionado, estadoSelecionado, cidadeSelecionada, pavilhaoSelecionado, table]);

  const { getHeaderGroups, getRowModel, getCanPreviousPage, getCanNextPage, getPageOptions, setPageIndex, nextPage, previousPage, getState } = table;

  const toggleCampo = (campoId) => {
    setCamposSelecionados(prev => prev.includes(campoId) ? prev.filter(id => id !== campoId) : [...prev, campoId]);
  };

  const ordenarDados = (dados) => {
    return [...dados].sort((a, b) => {
      let valorA = getNestedValue(a, ordenacao, '');
      let valorB = getNestedValue(b, ordenacao, '');
      if (typeof valorA === 'string' && valorA.startsWith('R$')) {
        valorA = parseFloat(valorA.replace(/[R$.]/g, '').replace(',', '.').trim());
        valorB = parseFloat(String(valorB).replace(/[R$.]/g, '').replace(',', '.').trim());
      }
      if (typeof valorA === 'string') return ordemDescendente ? valorB.localeCompare(valorA) : valorA.localeCompare(valorB);
      return ordemDescendente ? valorB - valorA : valorA - valorB;
    });
  };

  const calcularTodosResumos = (dadosOrdenados, camposSelecionados) => {
    let camposParaExportar = camposDisponiveis.filter(c => camposSelecionados.includes(c.id));
    
    const camposExpositorParaAgrupar = ['expositor.cnpj_codigo_internacional', 'expositor.nome_fantasia', 'expositor.endereco', 'expositor.email', 'expositor.telefone', 'expositor.whatsapp'];
    const camposExpositorSelecionados = camposParaExportar.filter(c => camposExpositorParaAgrupar.includes(c.id));

    if (camposExpositorSelecionados.length > 0) {
        const colunaAgrupada = {
            id: 'detalhesExpositor', label: 'Detalhes do Expositor',
            accessor: (item) => camposExpositorSelecionados.map(c => `${c.label}: ${getNestedValue(item, c.id, '')}`).filter(v => v.slice(-2) !== ': ').join('\n')
        };
        camposParaExportar = camposParaExportar.filter(c => !camposExpositorParaAgrupar.includes(c.id));
        const expositorIndex = camposParaExportar.findIndex(c => c.id === 'expositor.nome');
        camposParaExportar.splice(expositorIndex !== -1 ? expositorIndex + 1 : camposParaExportar.length, 0, colunaAgrupada);
    }

    // Agrupamento de Campos do Estande
    const camposEstandeParaAgrupar = [
        'estande_detalhado.valor', 'estande_detalhado.area', 'estande_detalhado.metragem_bonificada',
        'estande_detalhado.valor_kva', 'estande_detalhado.valor_estacionamento', 'estande_detalhado.valor_total_adicionais',
        'estande_detalhado.valor_final_calculado', 'estande_detalhado.quantidade_kva_tabela', 'estande_detalhado.quantidade_estacionamento',
    ];
    const camposEstandeSelecionados = camposParaExportar.filter(c => camposEstandeParaAgrupar.includes(c.id));
    // if (camposEstandeSelecionados.length > 0) {
    //     const colunaAgrupada = {
    //         id: 'detalhesEstande', label: 'Detalhes do Espaço',
    //         accessor: (item) => camposEstandeSelecionados.map(campo => {
    //             // Acessa o valor já formatado
    //             return `${campo.label}: ${campo.accessor(item)}`;
    //         }).join('\n')
    //     };
    //     camposParaExportar = camposParaExportar.filter(c => !camposEstandeParaAgrupar.includes(c.id));
    if (camposEstandeSelecionados.length > 0) {
        const colunaAgrupada = {
            id: 'detalhesEstande', label: 'Detalhes do Espaço',
            accessor: (item) => camposEstandeSelecionados.flatMap(campo => {
                // 1. Se for a coluna de "Valor Adicionais"
                if (campo.id === 'estande_detalhado.valor_total_adicionais') {
                    const adicionais = getNestedValue(item, 'estande_detalhado.adicionais_detalhados', []);
                    
                    // 2. Busca os adicionais detalhados e cria uma linha para cada um
                    if (Array.isArray(adicionais) && adicionais.length > 0) {
                        return adicionais.map(adicional => {
                            const nome = adicional.nome?.trim() || 'Adicional';
                            const valor = adicional.valor || 0;
                            return `Valor ${nome}: ${formatarMoeda(valor)}`;
                        });
                    }
                    // 3. Se não houver adicionais detalhados, não retorna nenhuma linha
                    return [];
                }
                
                // 4. Para todos os outros campos, retorna a linha normal (Ex: "Área (m²): 10,00")
                return `${campo.label}: ${campo.accessor(item)}`;
            }).join('\n') // Junta todas as linhas
        };
        camposParaExportar = camposParaExportar.filter(c => !camposEstandeParaAgrupar.includes(c.id));
        const espacoIndex = camposParaExportar.findIndex(c => c.id === 'estande_detalhado.nome');
        camposParaExportar.splice(espacoIndex !== -1 ? espacoIndex + 1 : camposParaExportar.length, 0, colunaAgrupada);
    }
     
    const dadosCancelados = dadosOrdenados.filter(isReservaCancelada);
    const dadosAtivos = dadosOrdenados.filter(item => !isReservaCancelada(item));
    const exibindoCanceladas = isFiltroCanceladas(statusSelecionado);

    /**
     * No relatório geral, cancelamentos aparecem na contagem histórica,
     * mas não entram na metragem nem no financeiro das reservas ativas.
     * Quando o filtro "Cancelada" está selecionado, os totais passam a
     * representar exclusivamente os registros cancelados exportados.
     */
    const dadosBaseTotais = exibindoCanceladas ? dadosCancelados : dadosAtivos;

    const totalRegistros = dadosOrdenados.length;
    const totalReservasAtivas = dadosAtivos.length;
    const totalCanceladas = dadosCancelados.length;
    const totalEspacosVendidos = dadosBaseTotais.length;

    const metragemVendida = dadosBaseTotais.reduce(
      (sum, item) => sum + toNumber(getNestedValue(item, 'estande_detalhado.area', 0)),
      0
    );
    const metragemBonificada = dadosBaseTotais.reduce(
      (sum, item) => sum + toNumber(getNestedValue(item, 'estande_detalhado.metragem_bonificada', 0)),
      0
    );
    const valorTotalVendido = dadosBaseTotais.reduce(
      (sum, item) => sum + getValorFinanceiroReal(item),
      0
    );
    const valorCalculadoItens = dadosBaseTotais.reduce(
      (sum, item) => sum + getValorCalculadoItens(item),
      0
    );
    const diferencaNegociacao = valorTotalVendido - valorCalculadoItens;
    const metragemCobrada = metragemVendida - metragemBonificada;
    const valorEspaco = dadosBaseTotais.reduce(
      (sum, item) => sum + toNumber(getNestedValue(item, 'estande_detalhado.valor_total', 0)),
      0
    );
    const valorKVA = dadosBaseTotais.reduce(
      (sum, item) => sum + toNumber(getNestedValue(item, 'estande_detalhado.valor_kva', 0)),
      0
    );
    const quantidadeKVA = dadosBaseTotais.reduce(
      (sum, item) => sum + toNumber(getNestedValue(item, 'estande_detalhado.quantidade_kva_tabela', 0)),
      0
    );
    const valorEstacionamento = dadosBaseTotais.reduce(
      (sum, item) => sum + toNumber(getNestedValue(item, 'estande_detalhado.valor_estacionamento', 0)),
      0
    );
    const quantidadeEstacionamento = dadosBaseTotais.reduce(
      (sum, item) => sum + toNumber(getNestedValue(item, 'estande_detalhado.quantidade_estacionamento', 0)),
      0
    );

    const totaisAdicionais = dadosBaseTotais.reduce((acc, item) => {
      const adicionais = getNestedValue(item, 'estande_detalhado.adicionais_detalhados', []);

      if (Array.isArray(adicionais)) {
        adicionais.forEach((adicional) => {
          const nome = adicional.nome?.trim() || 'Adicional';
          acc[nome] = (acc[nome] || 0) + toNumber(adicional.valor);
        });
      }

      return acc;
    }, {});

    const colunasParaSomar = camposAdicionais.filter(c => c.id.includes('valor') || c.id.includes('area') || c.id.includes('metragem')).map(c => c.id);
    
    const mainTableFootRow = camposParaExportar.map((campo, index) => {
        if (index === 0) return { content: 'TOTAL', isHeader: true };
        if (colunasParaSomar.includes(campo.id)) {
            const total = dadosBaseTotais.reduce((sum, item) => {
                if (typeof campo.getRawValue === 'function') {
                    return sum + (campo.getRawValue(item) || 0);
                }
                return sum + (getNestedValue(item, campo.id, 0) || 0);
            }, 0);
            const isCurrency = campo.accessor({}).toString().includes('R$');
            return { content: isCurrency ? formatarMoeda(total) : formatNumber(total), isCurrency };
        }
        return { content: '' };
    });

    // --- Resumo Agrupado ---
    let resumoAgrupado = null;
    if (agrupamento) {
        const nomeAgrupamento = { 'expositor.cidade': 'Cidade', 'expositor.estado': 'Estado', 'expositor.pais': 'País', 'comercial.nome': 'Vendedor' }[agrupamento];
        const dados = dadosBaseTotais.reduce((acc, item) => {
            const chave = getNestedValue(item, agrupamento, 'Não especificado');
            if (!acc[chave]) acc[chave] = { count: 0, area: 0, valor: 0 };
            acc[chave].count++;
            acc[chave].area += getNestedValue(item, 'estande_detalhado.area', 0) || 0;
            acc[chave].valor += getValorFinanceiroReal(item);
            return acc;
        }, {});
        resumoAgrupado = { nomeAgrupamento, dados };
    }

    return {
        geral: {
          totalRegistros,
          totalReservasAtivas,
          totalCanceladas,
          totalEspacosVendidos,
          exibindoCanceladas,
          rotuloItens: exibindoCanceladas
            ? 'Cancelados'
            : gerouVenda
              ? 'Vendidos'
              : 'Reservados',
          rotuloMetragem: exibindoCanceladas
            ? 'Cancelada'
            : gerouVenda
              ? 'Vendida'
              : 'Reservada',
          metragemVendida,
          metragemBonificada,
          metragemCobrada,
          valorTotalVendido,
          valorCalculadoItens,
          diferencaNegociacao,
          valorEspaco,
          valorKVA,
          quantidadeKVA,
          valorEstacionamento,
          quantidadeEstacionamento,
          totaisAdicionais
        },
        mainTableFooter: mainTableFootRow,
        agrupado: resumoAgrupado,
        camposParaExportar: camposParaExportar,
    };
  };

  const gerarPDF = async () => {
    const filteredData = table.getFilteredRowModel().rows.map(row => row.original);
    if (!filteredData || filteredData.length === 0) { toast.error('Nenhum dado para exportar'); return; }
    const dadosOrdenados = ordenarDados(filteredData);
    const resumos = calcularTodosResumos(dadosOrdenados, camposSelecionados);
    
    const camposParaExportar = resumos.camposParaExportar;

    const tableData = dadosOrdenados.map(item => camposParaExportar.map(campo => campo.accessor(item) ?? ''));
    
    const doc = new jsPDF({ orientation: "landscape", unit: 'mm', format: 'a4', compress: true });
    const margin = 10, pageWidth = doc.internal.pageSize.getWidth(), primaryColor = [54, 54, 54];
    
    // Configurações de estilo e cabeçalho...
    doc.setFont('helvetica', 'bold'); doc.setTextColor(...primaryColor); doc.setFontSize(16);
    doc.text(nomeRelatorio, pageWidth / 2, 15, { align: 'center' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    doc.text(`Gerado em: ${moment().format('DD/MM/YYYY HH:mm')}`, pageWidth - margin, 12, { align: 'right' });
    doc.setDrawColor(...primaryColor); doc.setLineWidth(0.5); doc.line(margin, 25, pageWidth - margin, 25);
    
    // --- Lógica da Tabela Principal ---
    const mainTableOptions = {
      startY: 30, 
      head: [camposParaExportar.map(c => c.label)], 
      body: tableData,
      margin: { left: margin, right: margin },
      headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 9 },
      footStyles: { fillColor: [230, 230, 230], textColor: primaryColor, fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8 }
    };

    const hasTotals = resumos.mainTableFooter.some(cell => !cell.isHeader && cell.content && cell.content.trim() !== '');

    if (hasTotals) {
        mainTableOptions.foot = [resumos.mainTableFooter.map(cell => {
            const styles = { halign: cell.isHeader ? 'left' : 'right' };
            return { content: cell.content, styles };
        })];
        mainTableOptions.showFoot = 'lastPage';
    }

    autoTable(doc, mainTableOptions);
    
    let yPosition = doc.lastAutoTable.finalY + 10;
    const checarNovaPagina = (pos, espaco) => {
        if (pos > doc.internal.pageSize.getHeight() - espaco) { doc.addPage(); return 20; }
        return pos;
    };
    yPosition = checarNovaPagina(yPosition, 80);
    const rGeral = resumos.geral;
    
    if (resumos.agrupado) {
        yPosition = checarNovaPagina(yPosition, 60);
        const rAgrupado = resumos.agrupado;
        doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text(`TOTAIS POR ${rAgrupado.nomeAgrupamento.toUpperCase()}`, margin, yPosition); yPosition += 8;
        
        const tableHead = [[
            { content: rAgrupado.nomeAgrupamento.toUpperCase(), styles: { halign: 'left' } },
            { content: 'REGISTROS', styles: { halign: 'center' } },
            { content: '%', styles: { halign: 'right' } },
            { content: 'METRAGEM', styles: { halign: 'right' } },
            { content: '%', styles: { halign: 'right' } },
            { content: 'VALOR', styles: { halign: 'right' } }
        ]];

        const chaves = Object.keys(rAgrupado.dados).sort((a, b) => rAgrupado.dados[b].count - rAgrupado.dados[a].count);
        const body = chaves.map((chave) => {
          const grupo = rAgrupado.dados[chave];
          const percentualRegistros = rGeral.totalEspacosVendidos > 0
            ? (grupo.count / rGeral.totalEspacosVendidos) * 100
            : 0;
          const percentualMetragem = rGeral.metragemVendida > 0
            ? (grupo.area / rGeral.metragemVendida) * 100
            : 0;

          return [
            chave,
            grupo.count,
            `${formatNumber(percentualRegistros)}%`,
            formatNumber(grupo.area),
            `${formatNumber(percentualMetragem)}%`,
            formatarMoeda(grupo.valor)
          ];
        });
        const foot = [[ { content: 'TOTAIS GERAIS', styles: { fontStyle: 'bold' } }, { content: rGeral.totalEspacosVendidos, styles: { halign: 'center' } }, { content: '100,00%', styles: { halign: 'right' } }, { content: formatNumber(rGeral.metragemVendida), styles: { halign: 'right' } }, { content: '100,00%', styles: { halign: 'right' } }, { content: formatarMoeda(rGeral.valorTotalVendido), styles: { halign: 'right' } } ]];

        autoTable(doc, {
            startY: yPosition, head: tableHead, body, foot,
            margin: { left: margin, right: margin }, theme: 'striped',
            headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 9, fontStyle: 'bold' },
            footStyles: { fillColor: [230, 230, 230], textColor: primaryColor, fontStyle: 'bold', fontSize: 9 },
            columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } }
        });
        
        yPosition = doc.lastAutoTable.finalY + 10;
        yPosition = checarNovaPagina(yPosition, 80);
    }

    // 2. DEPOIS, EXIBE O RESUMO GERAL
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('TOTAIS GERAIS', margin, yPosition); yPosition += 8;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    const drawLine = (label, value) => {
        doc.setFont('helvetica', 'bold'); doc.text(label, margin, yPosition);
        doc.setFont('helvetica', 'normal'); doc.text(String(value), margin + 60, yPosition, { align: 'left' }); yPosition += 6;
    };
    
    drawLine('Total de registros exportados', rGeral.totalRegistros);
    drawLine('Reservas ativas', rGeral.totalReservasAtivas);
    drawLine('Reservas canceladas', rGeral.totalCanceladas);
    drawLine('Espaços ' + rGeral.rotuloItens, rGeral.totalEspacosVendidos);
    drawLine('Metragem ' + rGeral.rotuloMetragem, formatNumber(rGeral.metragemVendida));
    drawLine('Metragem Bonificada', formatNumber(rGeral.metragemBonificada));
    drawLine('Metragem Cobrada', formatNumber(rGeral.metragemCobrada));
    drawLine('Valor Contratado / Financeiro', formatarMoeda(rGeral.valorTotalVendido));
    drawLine('Valor Calculado dos Itens', formatarMoeda(rGeral.valorCalculadoItens));
    drawLine('Diferença por Negociação', formatarMoeda(rGeral.diferencaNegociacao));
    drawLine('Valor Espaço', formatarMoeda(rGeral.valorEspaco));
    drawLine('Quantidade KVA', rGeral.quantidadeKVA);
    drawLine('Valor KVA', formatarMoeda(rGeral.valorKVA));
    drawLine('Quantidade Estacionamento', rGeral.quantidadeEstacionamento);
    drawLine('Valor Estacionamento', formatarMoeda(rGeral.valorEstacionamento));
    if (rGeral.totaisAdicionais) {
        Object.entries(rGeral.totaisAdicionais).forEach(([nome, total]) => {
            drawLine(`Valor ${nome}`, formatarMoeda(total));
        });
    }

    const pageHeight = doc.internal.pageSize.getHeight();
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i); doc.setFontSize(8); doc.setTextColor(100);
        doc.text(`Gerado em ${moment().format('DD/MM/YYYY HH:mm')}`, margin, pageHeight - 10, { align: 'left' });
        try { doc.addImage(logo, 'PNG', (pageWidth - 20) / 2, pageHeight - 15, 20, 8); } catch (e) { /* silent */ }
        doc.text(`Página ${i} de ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    }

    doc.save(`${nomeRelatorio}_${moment().format('YYYYMMDD_HHmm')}.pdf`);
    setIsExportacaoAvancadaOpen(false);
    toast.success('PDF gerado com sucesso!');
  };

  const exportarXLSX = () => {
    try {
      const filteredData = table.getFilteredRowModel().rows.map(row => row.original);
      if (!filteredData || filteredData.length === 0) {
        toast.error("Nenhum dado para exportar");
        return;
      }
      
      const dadosOrdenados = ordenarDados(filteredData);
      let camposExportar = camposDisponiveis.filter(c => camposSelecionados.includes(c.id));
      const resumos = calcularTodosResumos(dadosOrdenados, camposSelecionados);
      const dadosBaseTotais = resumos.geral.exibindoCanceladas
        ? dadosOrdenados.filter(isReservaCancelada)
        : dadosOrdenados.filter(item => !isReservaCancelada(item));

      const nomesAdicionaisUnicos = Array.from(new Set(
        dadosOrdenados.flatMap((item) => {
          const adicionais = getNestedValue(item, 'estande_detalhado.adicionais_detalhados', []);
          return Array.isArray(adicionais)
            ? adicionais.map(adicional => adicional.nome?.trim() || 'Adicional')
            : [];
        })
      ));

      // 2. Cria as definições das colunas dinâmicas
      const colunasAdicionaisDinamicas = nomesAdicionaisUnicos.map(nome => {
        const id = `adicional_dinamico_${nome.replace(/[^a-zA-Z0-9]/g, '_')}`;
        return {
            id: id,
            label: `Valor ${nome}`, // Ex: "Valor KVA Extra"
            
            // Accessor para o valor BRUTO (usado para somas e formatação numérica no Excel)
            getRawValue: (item) => {
                const adicionais = getNestedValue(item, 'estande_detalhado.adicionais_detalhados', []);
                const adicionalItem = Array.isArray(adicionais) ? adicionais.find(a => a.nome?.trim() === nome) : null;
                return adicionalItem ? adicionalItem.valor : null;
            },
            
            // Accessor para o valor FORMATADO (usado como fallback)
            accessor: (item) => {
                const adicionais = getNestedValue(item, 'estande_detalhado.adicionais_detalhados', []);
                const adicionalItem = Array.isArray(adicionais) ? adicionais.find(a => a.nome?.trim() === nome) : null;
                return formatarMoeda(adicionalItem ? adicionalItem.valor : null);
            },
            type: 'currency' // Marca como moeda para a formatação do Excel
        };
      });

      // 3. Encontra e substitui a coluna estática "Valor Adicionais" pelas dinâmicas
      const idTotalAdicionais = 'estande_detalhado.valor_total_adicionais';
      const indexTotalAdicionais = camposExportar.findIndex(c => c.id === idTotalAdicionais);
      
      if (indexTotalAdicionais !== -1) {
          // Substitui a 1 coluna estática pelas N colunas dinâmicas
          camposExportar.splice(indexTotalAdicionais, 1, ...colunasAdicionaisDinamicas);
      }

      const relatorioHeaders = camposExportar.map(c => c.label);
      const relatorioBody = dadosOrdenados.map(item =>
        camposExportar.map(campo => {
          const isNumericType = campo.type === 'currency' || campo.type === 'number';
          if (isNumericType) {
            // Se for um campo dinâmico (criado por nós), usa o getRawValue
            if (typeof campo.getRawValue === 'function') {
                return campo.getRawValue(item);
            }
            // Se for um campo estático, usa a lógica antiga
            return getNestedValue(item, campo.id, null);
          }
          // Para outros tipos (status, texto, data), usamos o acessor que formata o valor.
          return campo.accessor(item);
          // const isNumericType = campo.type === 'currency' || campo.type === 'number';
          // // Para XLSX, usamos números brutos para permitir formatação e fórmulas no Excel.
          // if (isNumericType) {
          //   return getNestedValue(item, campo.id, null);
          // }
          // // Para outros tipos (status, texto, data), usamos o acessor que formata o valor.
          // return campo.accessor(item);
        })
      );
      
      const relatorioFooter = camposExportar.map((campo, index) => {
        if (index === 0) return 'TOTAL';
        // const isNumericType = campo.type === 'currency' || campo.type === 'number';
        // if (isNumericType) {
        //   return dadosBaseTotais.reduce((sum, item) => sum + (getNestedValue(item, campo.id, 0) || 0), 0);
        // }
        const isNumericType = campo.type === 'currency' || campo.type === 'number';
        if (isNumericType) {
            let total;
            // Se for um campo dinâmico, usa o getRawValue para somar
            if (typeof campo.getRawValue === 'function') {
                total = dadosBaseTotais.reduce((sum, item) => sum + (campo.getRawValue(item) || 0), 0);
            } else {
            // Se for um campo estático, usa a lógica antiga
                total = dadosBaseTotais.reduce((sum, item) => sum + (getNestedValue(item, campo.id, 0) || 0), 0);
            }
            return total;
        }
        return null;
      });

      const relatorioSheetData = [relatorioHeaders, ...relatorioBody, relatorioFooter];
      const wsRelatorio = XLSX.utils.aoa_to_sheet(relatorioSheetData);
      
      // --- 2. Aplicar formatação de número/moeda na planilha "Relatório" ---
      const BRL_FORMAT = '"R$" #,##0.00';
      const NUM_FORMAT = '#,##0.00';
      const lastRow = relatorioSheetData.length - 1;

      camposExportar.forEach((campo, colIndex) => {
        if (campo.type === 'currency' || campo.type === 'number') {
          const format = campo.type === 'currency' ? BRL_FORMAT : NUM_FORMAT;
          for (let rowIndex = 1; rowIndex <= lastRow; rowIndex++) {
            const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
            const cell = wsRelatorio[cellAddress];
            if (cell && typeof cell.v === 'number') {
              cell.t = 'n';
              cell.z = format;
            }
          }
        }
      });

      const rGeral = resumos.geral;
      let resumosSheetData = [
        ['RESUMO GERAL'],
        ['Total de registros exportados', rGeral.totalRegistros],
        ['Reservas ativas', rGeral.totalReservasAtivas],
        ['Reservas canceladas', rGeral.totalCanceladas],
        ['Espaços ' + rGeral.rotuloItens, rGeral.totalEspacosVendidos],
        ['Metragem ' + rGeral.rotuloMetragem, rGeral.metragemVendida],
        ['Metragem Bonificada', rGeral.metragemBonificada],
        ['Metragem Cobrada', rGeral.metragemCobrada],
        ['Valor Contratado / Financeiro', rGeral.valorTotalVendido],
        ['Valor Calculado dos Itens', rGeral.valorCalculadoItens],
        ['Diferença por Negociação', rGeral.diferencaNegociacao],
        ['Valor Espaço', rGeral.valorEspaco],
        ['Valor KVA', rGeral.valorKVA],
        ['Quantidade KVA', rGeral.quantidadeKVA],
        ['Valor Estacionamento', rGeral.valorEstacionamento],
        ['Quantidade Estacionamento', rGeral.quantidadeEstacionamento],
      ];

      if (rGeral.totaisAdicionais) {
          Object.entries(rGeral.totaisAdicionais).forEach(([nome, total]) => {
              resumosSheetData.push([`Valor ${nome}`, total]);
          });
      }

      if (resumos.agrupado) {
        const rAgrupado = resumos.agrupado;
        resumosSheetData.push([]);
        resumosSheetData.push([`RESUMO AGRUPADO POR ${rAgrupado.nomeAgrupamento.toUpperCase()}`]);
        resumosSheetData.push([rAgrupado.nomeAgrupamento, 'Registros', 'Metragem', 'Valor']);

        Object.keys(rAgrupado.dados).sort((a,b) => a.localeCompare(b)).forEach(chave => {
            const grupo = rAgrupado.dados[chave];
            resumosSheetData.push([chave, grupo.count, grupo.area, grupo.valor]);
        });
        resumosSheetData.push(['TOTAL GERAL', rGeral.totalEspacosVendidos, rGeral.metragemVendida, rGeral.valorTotalVendido]);
      }
      const wsResumos = XLSX.utils.aoa_to_sheet(resumosSheetData);

      // --- 4. Aplicar formatação na planilha "Resumos" ---
      const rangeResumos = XLSX.utils.decode_range(wsResumos['!ref']);
      let inGroupedSummary = false;
      for (let R = rangeResumos.s.r; R <= rangeResumos.e.r; ++R) {
          const cellA = wsResumos[XLSX.utils.encode_cell({ r: R, c: 0 })];
          if (!cellA || !cellA.v) continue;

          if (cellA.v.toString().startsWith('RESUMO AGRUPADO')) {
              inGroupedSummary = true;
              continue;
          }

          if (inGroupedSummary) {
              const cellMetragem = wsResumos[XLSX.utils.encode_cell({ r: R, c: 2 })];
              if (cellMetragem && typeof cellMetragem.v === 'number') {
                  cellMetragem.t = 'n';
                  cellMetragem.z = NUM_FORMAT;
              }
              const cellValor = wsResumos[XLSX.utils.encode_cell({ r: R, c: 3 })];
              if (cellValor && typeof cellValor.v === 'number') {
                  cellValor.t = 'n';
                  cellValor.z = BRL_FORMAT;
              }
          } else {
              const label = cellA.v.toString().toLowerCase();
              const cellB = wsResumos[XLSX.utils.encode_cell({ r: R, c: 1 })];
              if (cellB && typeof cellB.v === 'number') {
                  if (label.includes('valor')) {
                      cellB.t = 'n';
                      cellB.z = BRL_FORMAT;
                  } else if (label.includes('metragem')) {
                      cellB.t = 'n';
                      cellB.z = NUM_FORMAT;
                  }
              }
          }
      }

      // --- 5. Auto-ajuste da largura das colunas ---
      const fitToColumn = (data) => {
        if (!data || data.length === 0) return [];
        const C_WIDTH = 1.1;
        return data[0].map((a, i) => ({
          wch: Math.max(...data.map(a2 => (a2[i] ? a2[i].toString().length : 0))) * C_WIDTH,
        }));
      };
      wsRelatorio['!cols'] = fitToColumn(relatorioSheetData);
      wsResumos['!cols'] = fitToColumn(resumosSheetData);

      // --- 6. Criar o Workbook e fazer o download ---
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, wsRelatorio, "Relatório");
      XLSX.utils.book_append_sheet(wb, wsResumos, "Resumos");

      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });

      saveAs(dataBlob, `${nomeRelatorio}_${moment().format('YYYYMMDD_HHmm')}.xlsx`);
      toast.success('XLSX exportado com sucesso!');
      setIsExportacaoAvancadaOpen(false);

    } catch (error) {
      console.error('Erro ao exportar XLSX:', error);
      toast.error('Falha ao exportar XLSX');
    }
  };

  const paisesFilterOptions = useMemo(() => paisesOptions.map(p => ({ value: p.sigla, label: p.nome || p.nome })), [paisesOptions]);
  const estadosFilterOptions = useMemo(() => estadosOptions.map(e => ({ value: e.sigla, label: e.nome })), [estadosOptions]);
  const cidadesFilterOptions = useMemo(() => cidadesOptions.map(c => ({ value: c.nome, label: c.nome })), [cidadesOptions]);
  const pavilhoesFilterOptions = useMemo(() => pavilhoesOptions.map(p => ({ value: p.nome, label: p.nome })), [pavilhoesOptions]);

  const getVisiblePages = (pageCount, currentIndex, max = 7) => {
    const last = pageCount - 1;
    if (pageCount <= max) return Array.from({ length: pageCount }, (_, i) => i);

    const windowSize = Math.max(1, Math.floor((max - 3) / 2)); // reserva 1ª, última e duas reticências
    let start = Math.max(1, currentIndex - windowSize);
    let end = Math.min(last - 1, currentIndex + windowSize);

    const need = (max - 2) - (end - start + 1);
    if (need > 0) {
      if (start === 1) {
        end = Math.min(last - 1, end + need);
      } else if (end === last - 1) {
        start = Math.max(1, start - need);
      }
    }

    const middle = Array.from({ length: end - start + 1 }, (_, i) => start + i);
    return [0, ...(start > 1 ? ['...'] : []), ...middle, ...(end < last - 1 ? ['...'] : []), last];
  };
  
  return (
    <Fragment>
      <ToastContainer closeButton={false} autoClose={3000} />
      {isGlobalFilter && (
        <Row className="mb-1">
          <CardBody className="border border-dashed border-end-0 border-start-0">
            <Row className="g-3">
              {feirasOptions.length > 0 && (
                <>
                  <Col md={3}>
                      <Select placeholder="Selecione a feira..." value={feirasOptions.find(f => f.value === feiraSelecionada)} onChange={option => onFeiraChange(option ? option.value : '')} options={feirasOptions} isClearable />
                  </Col>
                   </> 
              )}
              <Col md={3}>
                  <Select
                      placeholder="Todos os países"
                      value={paisesFilterOptions.find(p => p.value === paisSelecionado)}
                      onChange={option => onPaisChange(option ? option.value : '')}
                      options={paisesFilterOptions}
                      isDisabled={!feiraSelecionada && !dashboard}
                      isClearable
                  />
              </Col>
               
              {paisSelecionado === 'BR' && (
                <>
                  <Col md={3}>
                      <Select
                          placeholder="Todos os estados"
                          value={estadosFilterOptions.find(e => e.value === estadoSelecionado)}
                          onChange={option => onEstadoChange(option ? option.value : '')}
                          options={estadosFilterOptions}
                          isDisabled={estadosOptions.length === 0}
                          isClearable
                      />
                  </Col>
                  <Col md={3}>
                      <Select
                          placeholder="Todas as cidades"
                          value={cidadesFilterOptions.find(c => c.value === cidadeSelecionada)}
                          onChange={option => onCidadeChange(option ? option.value : '')}
                          options={cidadesFilterOptions}
                          isDisabled={cidadesOptions.length === 0}
                          isClearable
                      />
                  </Col>
                </>
              )}
              <Col md={3}>
                  <Select
                      placeholder="Todos os pavilhões"
                      value={pavilhoesFilterOptions.find(p => p.value === pavilhaoSelecionado)}
                      onChange={option => onPavilhaoChange(option ? option.value : '')}
                      options={pavilhoesFilterOptions}
                      isDisabled={!feiraSelecionada && !dashboard}
                      isClearable
                  />
              </Col>
{ statusOptions.length > 0 && (
    <Col md={3}>
      <Select
          placeholder="Todos os status"
          value={statusOptions.map(s => ({ value: s, label: s })).find(s => s.value === statusSelecionado)}
          onChange={option => onStatusChange(option ? option.value : '')}
          options={statusOptions.map(s => ({ value: s, label: s }))}
          isClearable
      />
  </Col>
)}
              <Col md={3}>
                <div className="search-box me-2 mb-2 d-inline-block col-12">
                  <Input value={globalFilter ?? ''} onChange={e => setGlobalFilter(e.target.value)} placeholder={SearchPlaceholder || "Pesquisar..."} className="form-control" />
                  <i className="bx bx-search-alt search-icon"></i>
                </div>
              </Col>
              <Col md={3} className="align-items-end">
                <Button color="primary" onClick={() => setIsExportacaoAvancadaOpen(true)} disabled={!data || data.length === 0}>
                  <i className="ri-file-pdf-line align-bottom me-1"></i> Exportar
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Row>
      )}

      <div className={divClassName}>
        <Table hover className={tableClassName}>
          <thead className={theadClassName}>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} {...{ onClick: header.column.getToggleSortingHandler() }}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{ asc: ' ↑', desc: ' ↓' }[header.column.getIsSorted()] ?? null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
        <Col sm>
          <div className="text-muted">
            Mostrando <span className="fw-semibold ms-1">{getState().pagination.pageSize}</span> de{" "}
            <span className="fw-semibold">{data.length}</span> Resultados
          </div>
        </Col>

        <Col sm="auto">
          <ul
            className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0"
            style={{ flexWrap: 'wrap', rowGap: '8px' }} // permite quebra em 2+ linhas se precisar
          >
            <li className={!getCanPreviousPage() ? "page-item disabled" : "page-item"}>
              <Link to="#" className="page-link" onClick={previousPage} aria-label="Página anterior">
                Anterior
              </Link>
            </li>

            {(() => {
              const pageCount = table.getPageCount();
              const current = getState().pagination.pageIndex;
              const pages = getVisiblePages(pageCount, current, 9); // aumente/diminua para mostrar mais/menos botões

              return pages.map((p, idx) => {
                if (p === '...') {
                  return (
                    <li key={`ellipsis-${idx}`} className="page-item disabled">
                      <span className="page-link">…</span>
                    </li>
                  );
                }
                const isActive = current === p;
                return (
                  <li key={p} className={`page-item${isActive ? ' active' : ''}`}>
                    <Link
                      to="#"
                      className={`page-link${isActive ? ' active' : ''}`}
                      onClick={() => setPageIndex(p)}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {p + 1}
                    </Link>
                  </li>
                );
              });
            })()}

            <li className={!getCanNextPage() ? "page-item disabled" : "page-item"}>
              <Link to="#" className="page-link" onClick={nextPage} aria-label="Próxima página">
                Próximo
              </Link>
            </li>
          </ul>
        </Col>
      </Row>
      <Modal isOpen={isExportacaoAvancadaOpen} toggle={() => setIsExportacaoAvancadaOpen(false)} size="lg">
        <ModalHeader toggle={() => setIsExportacaoAvancadaOpen(false)}>Exportação Avançada</ModalHeader>
        <ModalBody>
          <p className="text-muted">Selecione os campos e configure as opções para o seu relatório.</p>
          <Row>
            <Col md={6}>
              <Card>
                <CardHeader><h6 className="mb-0">Colunas da Tabela</h6></CardHeader>
                <CardBody style={{ minHeight: '220px', maxHeight: '220px', overflowY: 'auto' }}>
                  <FormGroup>
                    {camposDasColunas.map(campo => (
                      <div key={campo.id} className="form-check">
                        <Input type="checkbox" id={`campo-${campo.id}`} checked={camposSelecionados.includes(campo.id)} onChange={() => toggleCampo(campo.id)} />
                        <Label for={`campo-${campo.id}`} check>{campo.label}</Label>
                      </div>
                    ))}
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
            
            <Col md={6}>
              <Card>
                <CardHeader><h6 className="mb-0">Dados Adicionais</h6></CardHeader>
                <CardBody style={{ minHeight: '220px', maxHeight: '220px', overflowY: 'auto' }}>
                  <FormGroup>
                    {camposAdicionais.map(campo => (
                      <div key={campo.id} className="form-check">
                        <Input type="checkbox" id={`campo-${campo.id}`} checked={camposSelecionados.includes(campo.id)} onChange={() => toggleCampo(campo.id)} />
                        <Label for={`campo-${campo.id}`} check>{campo.label}</Label>
                      </div>
                    ))}
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>

            {/* --- LINHA 2: OPÇÕES DO RELATÓRIO --- */}
            <Col md={6} className="mt-3">
              <Card>
                <CardHeader><h6 className="mb-0">Opções de Ordenação</h6></CardHeader>
                <CardBody>
                  <FormGroup>
                    <Label for="ordenacao">Ordenar por:</Label>
                    <Input bsSize="sm" type="select" id="ordenacao" value={ordenacao} onChange={(e) => setOrdenacao(e.target.value)}>
                      {camposDisponiveis.map(campo => (<option key={campo.id} value={campo.id}>{campo.label}</option>))}
                    </Input>
                  </FormGroup>
                  <FormGroup check>
                    <Input type="checkbox" id="ordemDescendente" checked={ordemDescendente} onChange={() => setOrdemDescendente(!ordemDescendente)} />
                    <Label for="ordemDescendente" check>Ordem Decrescente</Label>
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>

            <Col md={6} className="mt-3">
              <Card>
                <CardHeader><h6 className="mb-0">Opções de Resumo</h6></CardHeader>
                <CardBody>
                  <FormGroup>
                    <Label for="agrupamento">Agrupar totais por:</Label>
                    <Input bsSize="sm" type="select" id="agrupamento" value={agrupamento} onChange={(e) => setAgrupamento(e.target.value)}>
                      <option value="">Nenhum</option>
                      <option value="expositor.cidade">Cidade</option>
                      <option value="expositor.estado">Estado</option>
                      <option value="expositor.pais">País</option>
                      <option value="comercial.nome">Vendedor</option>
                    </Input>
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsExportacaoAvancadaOpen(false)}>Cancelar</Button>
          <div className="d-flex gap-2">
            <Button color="primary" onClick={gerarPDF}><i className="ri-file-pdf-line align-bottom me-1"></i> PDF</Button>
            <Button color="success" onClick={exportarXLSX}>
              <i className="ri-file-excel-2-line align-bottom me-1"></i> Excel
            </Button>
            {/* <Button color="info" onClick={exportarXML}><i className="ri-code-line align-bottom me-1"></i> XML</Button> */}
          </div>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default TabelaFiltroPaginacao;
